// Copyright ©️ 2026 eVolve MEP, LLC

import { type NotificationSettings, useEvolveGet, useEvolvePut } from '@evolvemep/api-ts';
import { Box, LoadingOverlay } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useFormModal } from 'components/Mantine/Modals/FormModal';
import { isNil } from 'helpers/isNotNil';

import { NotificationSettingsSection } from './NotificationSettingsSection';
import { NotificationSettingsToggleRow } from './NotificationSettingsToggleRow';

const NotificationSettingsFormFields = ({ settings }: { settings: NotificationSettings }) => {
  const { apiCall: saveSettings } = useEvolvePut('shop/notificationSettings');

  const form = useForm<NotificationSettings>({ initialValues: settings });

  const onSubmit = async ({ channels, notificationTypes }: typeof form.values) => {
    await saveSettings({
      channels: channels.map(({ notificationChannelTypeId, isEnabled }) => ({
        notificationChannelTypeId,
        isEnabled,
      })),
      notificationTypes: notificationTypes.map(({ notificationTypeId, isEnabled }) => ({
        notificationTypeId,
        isEnabled,
      })),
    });
    notifications.show({
      title: 'Successfully saved notification settings',
      message: 'Your notification settings have been updated.',
      color: 'green',
    });
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <NotificationSettingsSection title="Notification Channels">
        {form.values.channels.map(({ notificationChannelTypeId, name }, index) => (
          <NotificationSettingsToggleRow
            key={notificationChannelTypeId}
            label={name}
            {...form.getInputProps(`channels.${index}.isEnabled`, { type: 'checkbox' })}
          />
        ))}
      </NotificationSettingsSection>
      <NotificationSettingsSection title="Work Order Status Updates">
        {form.values.notificationTypes.map(({ notificationTypeId, name }, index) => (
          <NotificationSettingsToggleRow
            key={notificationTypeId}
            label={name}
            {...form.getInputProps(`notificationTypes.${index}.isEnabled`, { type: 'checkbox' })}
          />
        ))}
      </NotificationSettingsSection>
    </>
  );
};

export const NotificationSettingsForm = () => {
  const { data: settings } = useEvolveGet('shop/notificationSettings', { lazy: false });

  if (isNil(settings)) {
    return (
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible />
      </Box>
    );
  }
  return <NotificationSettingsFormFields settings={settings} />;
};
